import { ChangeEvent, FC, KeyboardEvent, PointerEvent, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Checkbox } from 'src/components/widgets/checkbox';
import { QuestionDialog, useDialogManager } from 'src/components/widgets/dialog';
import { Icon } from 'src/components/widgets/icon';
import Stack from 'src/components/widgets/stack/stack';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { stringFormat, T, uploadFile } from 'src/shared/helpers';
import ZoomInIcon from 'src/shared/material_icons/action/zoom_in.svg';
import AddIcon from 'src/shared/material_icons/content/add.svg';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import ReplaceImageIcon from 'src/shared/material_icons/image/photo_library.svg';
import ChevronLeftIcon from 'src/shared/material_icons/navigation/chevron_left.svg';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import styled from 'styled-components';
import { AgentImageZoomDialog, AgentZoomVariant } from './AgentImageZoomDialog';
import {
    AreaRectangle,
    clampValue,
    drawRectangle,
    ImagePoint,
    ImageSize,
    isValidRectangle,
    moveRectangle,
    ResizeHandle,
    resizeRectangle,
    setRectangleSize
} from './printAreaGeometry';
import { AgentGeneratedImage, AgentProductPreviewActions, ProductArea, ProductSide } from './types';

const MAX_IMAGE_BYTES = 12 * 1024 * 1024;
const ZOOM_DIALOG_ID = 'agent-artifact-image-zoom';
const HANDLES: Array<{ key: ResizeHandle; x: number; y: number; label: string }> = [
    { key: 'nw', x: 0, y: 0, label: T._('Resize from top left', 'Admin') },
    { key: 'n', x: 0.5, y: 0, label: T._('Resize from top', 'Admin') },
    { key: 'ne', x: 1, y: 0, label: T._('Resize from top right', 'Admin') },
    { key: 'e', x: 1, y: 0.5, label: T._('Resize from right', 'Admin') },
    { key: 'se', x: 1, y: 1, label: T._('Resize from bottom right', 'Admin') },
    { key: 's', x: 0.5, y: 1, label: T._('Resize from bottom', 'Admin') },
    { key: 'sw', x: 0, y: 1, label: T._('Resize from bottom left', 'Admin') },
    { key: 'w', x: 0, y: 0.5, label: T._('Resize from left', 'Admin') }
];

interface AgentProductSidePreviewProps extends AgentProductPreviewActions {
    variantRef: string;
    side: ProductSide;
    revisionNumber: number | null;
    disabled?: boolean;
    /** A historical snapshot: the image cannot be edited directly (no drawing/dragging/resizing), but the action chips
     * still work, describing the change to the agent. Distinct from `disabled`, which also blocks the chips while busy. */
    readOnly?: boolean;
    /** Quick navigation between the sides of the variant; omitted when there is only one side. */
    sideNavigation?: { position: number; total: number; onPrevious: () => void; onNext: () => void };
    /** The latest image generation for this side, from the conversation: explains a missing image. */
    imageGeneration?: AgentGeneratedImage;
    /** Every variant with its side images, so the zoom dialog can move between variants and sides. */
    zoomVariants?: AgentZoomVariant[];
    /** When set, the print-area action chips render here (in the conversation) instead of inside the preview. */
    actionsSlot?: HTMLElement | null;
    /** The preview is read-only: its actions describe the change to the agent, which makes it in the conversation. */
    onRequestEdit?: (message: string) => void;
}
interface AreaEdit {
    areaRef: string | null;
    rect: AreaRectangle;
    name: string;
    revision: number;
    unit: 'cm' | 'px';
    ratio: number | null;
    /** On: the area stays as drawn (the ruler is fixed) and centimetres change the real size of the side. Off: centimetres resize the area. */
    keepScale: boolean;
    /** The scale the typed centimetres imply, saved with the area. */
    pendingScale: number | null;
}
interface Drag {
    mode: 'draw' | 'move' | ResizeHandle;
    start: ImagePoint;
    original: AreaRectangle;
    pointerId: number;
}
// The grey of the surrounding panel shows through the stage band; sections are flat white bands.
const StageBand = styled.div`
    display: flex;
    flex-shrink: 0;
    min-width: 0;
    flex-direction: column;
    padding: 14px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;
const DetailsColumn = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
`;
const Stage = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    min-height: 120px;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 14px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    background: white;
    user-select: none;
`;
const Section = styled(Stack)`
    flex-shrink: 0;
    min-width: 0;
    padding: 16px 20px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    background: white;
`;
// Image and side data sit next to each other when the preview is wide enough (container declared by AgentTaskPreview).
// Fills the rest of the artifact: side by side, the stage takes the whole height of the slot.
const Layout = styled.div`
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;

    @container agent-preview (min-width: 480px) {
        display: grid;
        grid-template-columns: minmax(0, 3fr) minmax(200px, 1fr);
        border-bottom: 1px solid ${(props) => props.theme.app.borderColor};

        ${StageBand} {
            border-bottom: 0;
        }

        ${DetailsColumn} {
            border-left: 1px solid ${(props) => props.theme.app.borderColor};
            background: white;
        }

        ${DetailsColumn} > ${Section}:last-child {
            border-bottom: 0;
        }
    }
`;
const NavArrow = styled.button<{ $side: 'left' | 'right' }>`
    position: absolute;
    top: 50%;
    ${(props) => props.$side}: 10px;
    z-index: 5;
    display: grid;
    width: 32px;
    height: 32px;
    place-items: center;
    padding: 0;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 50%;
    background: white;
    box-shadow: 0 2px 6px rgba(38, 50, 56, 0.12);
    color: #455a64;
    cursor: pointer;
    transform: translateY(-50%);

    &:hover:not(:disabled) {
        border-color: #b0bec5;
        color: #263238;
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }

    &:focus-visible {
        outline: 2px solid #1769aa;
        outline-offset: 1px;
    }
`;
const ZoomButton = styled(NavArrow)`
    top: 10px;
    right: 10px;
    left: auto;
    transform: none;
`;
const SidePosition = styled.span`
    position: absolute;
    bottom: 10px;
    left: 50%;
    z-index: 5;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(38, 50, 56, 0.7);
    color: white;
    font-size: 11px;
    transform: translateX(-50%);
    pointer-events: none;
`;
const Tile = styled.button<{ $selected: boolean }>`
    display: flex;
    min-width: 0;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
    border: 1px solid ${(props) => (props.$selected ? props.theme.app.secondary : props.theme.app.borderColor)};
    border-radius: 12px;
    background: white;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
        border-color: ${(props) => (props.$selected ? props.theme.app.secondary : '#b0bec5')};
    }

    &:focus-visible {
        outline: 2px solid #1769aa;
        outline-offset: 1px;
    }
`;
const TileStage = styled.div`
    display: flex;
    height: 160px;
    align-items: center;
    justify-content: center;
    padding: 10px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;
const TileFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    color: #263238;
    font-size: 13px;
`;
const SectionTitle = styled.h4`
    margin: 0;
    color: #90a4ae;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;
const SubTitle = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    color: #263238;
    font-size: 13px;
    font-weight: 600;
`;
const Muted = styled.span`
    color: #78909c;
    font-size: 12px;
    font-weight: 400;
`;
const AreaList = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 -8px;
`;
const AreaRow = styled.div<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 34px;
    padding: 2px 8px;
    border-radius: 6px;
    background: ${(props) => (props.$selected ? '#f5f7fa' : 'transparent')};
    color: #263238;
    font-size: 13px;
    cursor: pointer;

    &:hover {
        background: #f5f7fa;
    }

    &:focus-visible {
        outline: 2px solid #1769aa;
    }
`;
const AreaIndex = styled.span`
    display: inline-flex;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: #fef2ed;
    color: #de5900;
    font-size: 11px;
    font-weight: 600;
`;
const AreaInfo = styled.span`
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
`;
const AreaName = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
const ImageFrame = styled.div<{ $aspectRatio: number; $drawing: boolean; $maxHeight?: number }>`
    position: relative;
    width: min(100%, ${(props) => (props.$maxHeight ?? 400) * props.$aspectRatio}px);
    max-height: ${(props) => props.$maxHeight ?? 400}px;
    aspect-ratio: ${(props) => props.$aspectRatio};
    cursor: ${(props) => (props.$drawing ? 'crosshair' : 'default')};
    touch-action: none;
    > img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        pointer-events: none;
    }
`;
const Rectangle = styled.div<{ $rect: AreaRectangle; $image: ImageSize; $active?: boolean; $selected?: boolean; $interactive: boolean }>`
    position: absolute;
    box-sizing: border-box;
    left: ${(props) => (props.$rect.x / props.$image.width) * 100}%;
    top: ${(props) => (props.$rect.y / props.$image.height) * 100}%;
    width: ${(props) => (props.$rect.width / props.$image.width) * 100}%;
    height: ${(props) => (props.$rect.height / props.$image.height) * 100}%;
    border: 2px ${(props) => (props.$active || props.$selected ? 'solid' : 'dashed')} ${(props) => (props.$active ? '#d96b16' : '#00899d')};
    background: ${(props) => (props.$active ? 'rgba(217, 107, 22, 0.12)' : 'rgba(0, 137, 157, 0.06)')};
    z-index: ${(props) => (props.$active ? 3 : props.$selected ? 2 : 1)};
    cursor: ${(props) => (props.$active ? 'move' : 'pointer')};
    pointer-events: ${(props) => (props.$interactive ? 'auto' : 'none')};
    &:focus-visible {
        outline: 3px solid #1769aa;
        outline-offset: 3px;
    }
`;
const Handle = styled.button<{ $x: number; $y: number; $handle: ResizeHandle }>`
    position: absolute;
    left: ${(props) => props.$x * 100}%;
    top: ${(props) => props.$y * 100}%;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    background: transparent;
    transform: translate(-50%, -50%);
    cursor: ${(props) => props.$handle}-resize;
    touch-action: none;
    &::after {
        content: '';
        position: absolute;
        inset: 6px;
        border: 2px solid #d96b16;
        border-radius: 2px;
        background: white;
    }
    &:focus-visible {
        outline: 2px solid #1769aa;
        border-radius: 3px;
    }
`;
const AreaLabel = styled.span`
    position: absolute;
    left: -2px;
    top: -2px;
    max-width: 170px;
    padding: 2px 5px;
    transform: translateY(-100%);
    background: #00899d;
    color: white;
    border-radius: 3px 3px 0 0;
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    pointer-events: none;
`;
// The print-area actions read as chips in the conversation, not toolbar buttons.
const ChipBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

const ActionChip = styled.button<{ $tone?: 'primary' | 'danger' }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 12px;
    border: 1px solid ${(props) => (props.$tone === 'primary' ? 'transparent' : props.theme.app.borderColor)};
    border-radius: 999px;
    background: ${(props) => (props.$tone === 'primary' ? props.theme.ai.solid : 'white')};
    color: ${(props) => (props.$tone === 'primary' ? 'white' : props.$tone === 'danger' ? '#de1c22' : props.theme.app.textColor)};
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;

    ${Icon},
    ${Icon} svg {
        width: 15px;
        height: 15px;
    }

    &:hover:not(:disabled) {
        background: ${(props) => (props.$tone === 'primary' ? props.theme.ai.solid : props.$tone === 'danger' ? '#fff5f5' : '#f5f3ff')};
        ${(props) => props.$tone === 'primary' && 'filter: brightness(0.96);'}
    }

    &:disabled {
        opacity: ${(props) => props.theme.app.disabledOpacity};
        cursor: default;
    }
`;
const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
`;
const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    color: #455967;
    font-size: 11px;
`;
const TextInput = styled.input`
    width: 100%;
    min-width: 0;
    height: 34px;
    box-sizing: border-box;
    padding: 0 9px;
    border: 1px solid #bac8d2;
    border-radius: 6px;
    background: white;
    color: #263746;
    font-size: 12px;
    &:focus-visible {
        outline: 2px solid #00899d;
        outline-offset: 1px;
    }
    &:disabled {
        background: #f2f5f7;
        color: #89969e;
    }
`;
const Select = styled.select`
    width: 100%;
    min-width: 0;
    height: 34px;
    padding: 0 8px;
    border: 1px solid #bac8d2;
    border-radius: 6px;
    background: white;
    color: #263746;
    font-size: 12px;
`;
const Hint = styled.p`
    margin: 0;
    color: #647783;
    font-size: 11px;
    line-height: 1.5;
`;
const ErrorText = styled(Hint)`
    color: #b42318;
`;
const MissingImageState = styled(Stack)`
    max-width: 320px;
    align-items: center;
    text-align: center;
`;

const generationFailureReason = (generation: AgentGeneratedImage) => {
    if (generation.errorCode === 'addon_not_active') return T._('AI Studio is not active.', 'Admin');
    if (generation.errorCode === 'no_credits') return T._('Not enough AI credits.', 'Admin');
    return generation.errorMessage;
};

/** Why a side has no image: the generation the conversation reported failed, is still running, or nothing was asked yet. */
const MissingImage: FC<{ generation?: AgentGeneratedImage; compact?: boolean }> = ({ generation, compact }) => {
    const state = generation?.state.toLowerCase() ?? '';
    const failed = Boolean(generation && (generation.errorCode || generation.errorMessage || ['failed', 'error', 'cancelled', 'canceled'].includes(state)));
    if (generation && failed) {
        const reason = generationFailureReason(generation);
        return (
            <MissingImageState $gap='4px'>
                <ErrorText>
                    <strong>{T._('Image generation failed', 'Admin')}</strong>
                </ErrorText>
                {!compact && reason && <ErrorText>{reason}</ErrorText>}
                {!compact && <Hint>{T._('Choose how to continue in the conversation.', 'Admin')}</Hint>}
            </MissingImageState>
        );
    }
    if (generation && state !== 'completed') return <Hint>{T._('Generating image…', 'Admin')}</Hint>;
    return <Hint>{T._('No image yet', 'Admin')}</Hint>;
};

const HiddenFileInput = styled.input`
    display: none;
`;

const numberText = (value: number, precision = 3) => Number(value.toFixed(precision)).toString();
const parseNumber = (value: string) => (value.trim() ? Number(value.replace(',', '.')) : NaN);
const areaName = (area: ProductArea, index: number) => area.name || area.label || `${T._('Print area', 'Admin')} ${index + 1}`;
const rectangleOf = (area: ProductArea | null, image: ImageSize): AreaRectangle | null => {
    const raw = area?.bounds?.raw;
    if (!raw || ![raw.x, raw.y, raw.width, raw.height].every(Number.isFinite) || raw.width <= 0 || raw.height <= 0) return null;
    return { x: raw.x + image.width / 2, y: raw.y + image.height / 2, width: raw.width, height: raw.height };
};
const readImageDimensions = (file: File) =>
    new Promise<{ widthPx: number; heightPx: number }>((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ widthPx: image.naturalWidth, heightPx: image.naturalHeight });
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error(T._('The selected image could not be read.', 'Admin')));
        };
        image.src = objectUrl;
    });

export const AgentProductSidePreview: FC<AgentProductSidePreviewProps> = ({
    variantRef,
    side,
    revisionNumber,
    disabled,
    readOnly,
    onUpsertPrintArea,
    onDeletePrintArea,
    onRestorePrintArea,
    onSetSideScale,
    onReplaceSideImage,
    sideNavigation,
    zoomVariants,
    imageGeneration,
    actionsSlot,
    onRequestEdit
}) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragRef = useRef<Drag | null>(null);
    const savingRef = useRef(false);
    const { showDialog, closeDialog } = useDialogManager();
    const areas = side.areas ?? [];
    const image = side.image;
    const imageSize = { width: image?.widthPx ?? 0, height: image?.heightPx ?? 0 };
    const hasImageDimensions = imageSize.width > 0 && imageSize.height > 0;
    const scale = side.resolution?.pixelsPerCm;
    const hasScale = Boolean(scale && Number.isFinite(scale) && scale > 0);
    const [selectedRef, setSelectedRef] = useState<string | null>(areas[0]?.entityReferenceKey ?? null);
    const selectedArea = areas.find((area) => area.entityReferenceKey === selectedRef) ?? areas[0] ?? null;
    const [edit, setEdit] = useState<AreaEdit | null>(null);
    const [widthText, setWidthText] = useState('');
    const [heightText, setHeightText] = useState('');
    const [geometryError, setGeometryError] = useState<string | null>(null);
    const [localError, setLocalError] = useState<string | null>(null);
    const [working, setWorking] = useState(false);
    const [undo, setUndo] = useState<{ areaRef: string; name: string; revision: number } | null>(null);
    const busy = Boolean(disabled || working);
    // A read-only snapshot can never be manipulated directly, whatever the draft carries.
    const canEdit = Boolean(!readOnly && variantRef && side.entityReferenceKey && revisionNumber && image?.url && hasImageDimensions);
    const staleEdit = Boolean(edit && edit.revision !== revisionNumber);
    const canManipulate = canEdit && !busy && !staleEdit;
    // With the scale fixed the area's size is what the typed centimetres measure: it can move, not be resized.
    const isSizeLocked = Boolean(edit && edit.unit === 'cm' && edit.keepScale);
    const canResize = canManipulate && !isSizeLocked;
    // While editing, the scale the typed centimetres imply replaces the saved one.
    const editScale = edit?.pendingScale ?? (hasScale ? scale! : null);
    const factor = edit?.unit === 'cm' && editScale ? editScale : 1;

    const syncDimensions = (rect: AreaRectangle, unit: 'cm' | 'px', pixelsPerCm: number | null = editScale) => {
        // Centimetres without a scale are unknown until the merchant types them.
        if (unit === 'cm' && !pixelsPerCm) {
            setWidthText('');
            setHeightText('');
        } else {
            const divisor = unit === 'cm' ? pixelsPerCm! : 1;
            setWidthText(numberText(rect.width / divisor));
            setHeightText(numberText(rect.height / divisor));
        }
        setGeometryError(null);
    };
    const updateRectangle = (rect: AreaRectangle) => {
        if (!edit) return;
        setEdit({ ...edit, rect });
        syncDimensions(rect, edit.unit);
    };
    const startEditing = (area: ProductArea | null) => {
        if (!canEdit || busy || (area && !area.entityReferenceKey)) return;
        const rect = rectangleOf(area, imageSize) ?? {
            x: imageSize.width * 0.3,
            y: imageSize.height * 0.3,
            width: imageSize.width * 0.4,
            height: imageSize.height * 0.4
        };
        const unit = hasScale ? 'cm' : 'px';
        setEdit({
            areaRef: area?.entityReferenceKey ?? null,
            rect,
            name: area ? area.name || area.label || '' : `${T._('Print area', 'Admin')} ${areas.length + 1}`,
            revision: revisionNumber!,
            unit,
            ratio: null,
            // Without a scale, the typed centimetres can only define it.
            keepScale: !hasScale,
            pendingScale: null
        });
        syncDimensions(rect, unit, hasScale ? scale! : null);
        setLocalError(null);
        if (area?.entityReferenceKey) setSelectedRef(area.entityReferenceKey);
    };
    const stopEditing = () => {
        dragRef.current = null;
        setEdit(null);
        setGeometryError(null);
        setLocalError(null);
    };
    const eventPoint = (event: PointerEvent): ImagePoint => {
        const box = stageRef.current!.getBoundingClientRect();
        return {
            x: clampValue((event.clientX - box.left) / box.width, 0, 1) * imageSize.width,
            y: clampValue((event.clientY - box.top) / box.height, 0, 1) * imageSize.height
        };
    };
    const beginDrag = (event: PointerEvent, mode: Drag['mode'], rect: AreaRectangle) => {
        if (!canManipulate || event.button !== 0 || (mode !== 'move' && !canResize)) return;
        event.preventDefault();
        event.stopPropagation();
        dragRef.current = { mode, original: rect, start: eventPoint(event), pointerId: event.pointerId };
        stageRef.current?.setPointerCapture(event.pointerId);
    };
    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || !edit || !canManipulate || event.pointerId !== drag.pointerId) return;
        const point = eventPoint(event);
        const dx = point.x - drag.start.x;
        const dy = point.y - drag.start.y;
        updateRectangle(
            drag.mode === 'draw'
                ? drawRectangle(drag.start, point, imageSize, edit.ratio)
                : drag.mode === 'move'
                  ? moveRectangle(drag.original, dx, dy, imageSize)
                  : resizeRectangle(drag.original, drag.mode, dx, dy, imageSize, edit.ratio)
        );
    };
    const finishDrag = (event: PointerEvent<HTMLDivElement>, cancel = false) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        if (cancel) updateRectangle(drag.original);
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };
    const onGeometryKey = (event: KeyboardEvent, handle?: ResizeHandle) => {
        if (!edit || !canManipulate) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            stopEditing();
            return;
        }
        const delta = event.shiftKey ? 10 : 1;
        const dx = event.key === 'ArrowLeft' ? -delta : event.key === 'ArrowRight' ? delta : 0;
        const dy = event.key === 'ArrowUp' ? -delta : event.key === 'ArrowDown' ? delta : 0;
        if (!dx && !dy) return;
        event.preventDefault();
        event.stopPropagation();
        if (handle && !canResize) return;
        updateRectangle(handle ? resizeRectangle(edit.rect, handle, dx, dy, imageSize, edit.ratio) : moveRectangle(edit.rect, dx, dy, imageSize));
    };
    const changeDimension = (axis: 'width' | 'height', text: string) => {
        if (!edit) return;
        // Scale fixed: the area stays as drawn and the typed size defines how many pixels make a centimetre.
        if (edit.unit === 'cm' && edit.keepScale) {
            const centimetres = parseNumber(text);
            if (axis === 'width') setWidthText(text);
            else setHeightText(text);
            if (!(centimetres > 0)) {
                setGeometryError(T._('Enter a positive size in centimetres.', 'Admin'));
                return;
            }
            const pixelsPerCm = (axis === 'width' ? edit.rect.width : edit.rect.height) / centimetres;
            setEdit({ ...edit, pendingScale: pixelsPerCm });
            if (axis === 'width') setHeightText(numberText(edit.rect.height / pixelsPerCm));
            else setWidthText(numberText(edit.rect.width / pixelsPerCm));
            setGeometryError(null);
            return;
        }
        let nextWidth = axis === 'width' ? text : widthText;
        let nextHeight = axis === 'height' ? text : heightText;
        if (edit.ratio && Number.isFinite(parseNumber(text))) {
            if (axis === 'width') nextHeight = numberText(parseNumber(text) / edit.ratio, 6);
            else nextWidth = numberText(parseNumber(text) * edit.ratio, 6);
        }
        setWidthText(nextWidth);
        setHeightText(nextHeight);
        const widthPx = parseNumber(nextWidth) * factor;
        const heightPx = parseNumber(nextHeight) * factor;
        const rect = setRectangleSize(edit.rect, widthPx, heightPx, imageSize);
        if (!rect) {
            if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || widthPx <= 0 || heightPx <= 0) {
                setGeometryError(T._('Enter a positive width and height.', 'Admin'));
            } else if (widthPx < 1 || heightPx < 1) {
                setGeometryError(T._('The print area must be at least 1 pixel wide and high.', 'Admin'));
            } else {
                const limitedAxis = edit.ratio ? axis : widthPx > imageSize.width ? 'width' : 'height';
                const maxPx =
                    limitedAxis === 'width'
                        ? Math.min(imageSize.width, edit.ratio ? imageSize.height * edit.ratio : imageSize.width)
                        : Math.min(imageSize.height, edit.ratio ? imageSize.width / edit.ratio : imageSize.height);
                const max = numberText(maxPx / factor, 6);
                setGeometryError(
                    edit.ratio
                        ? stringFormat(
                              T._('With the current proportions, {0} cannot exceed {1} {2} on this side.', 'Admin'),
                              limitedAxis === 'width' ? T._('width', 'Admin') : T._('height', 'Admin'),
                              max,
                              edit.unit
                          )
                        : stringFormat(
                              T._('{0} cannot exceed {1} {2} on this side.', 'Admin'),
                              limitedAxis === 'width' ? T._('Width', 'Admin') : T._('Height', 'Admin'),
                              max,
                              edit.unit
                          )
                );
            }
            return;
        }
        setGeometryError(null);
        setEdit({ ...edit, rect });
    };
    const changeRatio = (value: string) => {
        if (!edit) return;
        const ratio = value === 'free' ? null : value === 'current' ? edit.rect.width / edit.rect.height : Number(value);
        let rect = edit.rect;
        if (ratio) {
            const width = Math.min(rect.width, imageSize.height * ratio);
            const resized = setRectangleSize(rect, width, width / ratio, imageSize);
            if (!resized) return;
            rect = resized;
        }
        setEdit({ ...edit, rect, ratio });
        syncDimensions(rect, edit.unit);
    };
    const perform = async (action: () => Promise<void>) => {
        if (savingRef.current || busy) return;
        savingRef.current = true;
        setWorking(true);
        setLocalError(null);
        try {
            await action();
        } catch (error) {
            setLocalError((error as Error).message || T._('The draft could not be updated.', 'Admin'));
        } finally {
            savingRef.current = false;
            setWorking(false);
        }
    };
    const saveArea = () =>
        perform(async () => {
            if (!edit || staleEdit || geometryError || !isValidRectangle(edit.rect, imageSize)) return;
            const result = await onUpsertPrintArea({
                variantRef,
                sideRef: side.entityReferenceKey!,
                areaRef: edit.areaRef,
                expectedRevisionNumber: edit.revision,
                name: edit.name.trim(),
                x1: edit.rect.x,
                y1: edit.rect.y,
                x2: edit.rect.x + edit.rect.width,
                y2: edit.rect.y + edit.rect.height
            });
            if (!result) {
                setLocalError(T._('The area was not saved. Check the conversation message and try again.', 'Admin'));
                return;
            }
            setSelectedRef(result.areaRef ?? null);
            setUndo(null);
            // The sizes typed with the scale unlocked recalibrate the side, after the area they were measured on.
            if (edit.pendingScale && edit.pendingScale !== scale) {
                const scaled = await onSetSideScale({
                    variantRef,
                    sideRef: side.entityReferenceKey!,
                    pixelsPerCm: edit.pendingScale,
                    expectedRevisionNumber: result.revisionNumber
                });
                if (!scaled) {
                    setLocalError(T._('The area was saved, but the new scale could not be saved.', 'Admin'));
                    return;
                }
            }
            stopEditing();
        });
    const deleteArea = (area: ProductArea) =>
        perform(async () => {
            if (!area.entityReferenceKey || !revisionNumber) return;
            const result = await onDeletePrintArea({
                variantRef,
                sideRef: side.entityReferenceKey!,
                areaRef: area.entityReferenceKey,
                expectedRevisionNumber: revisionNumber
            });
            if (result) {
                setUndo({
                    areaRef: area.entityReferenceKey,
                    name: areaName(area, areas.indexOf(area)),
                    revision: result.revisionNumber
                });
                setSelectedRef(null);
                // Deleted from inside its editor: the editor has nothing left to edit.
                if (edit?.areaRef === area.entityReferenceKey) stopEditing();
            } else setLocalError(T._('The area could not be deleted.', 'Admin'));
        });
    const editedArea = edit?.areaRef ? areas.find((area) => area.entityReferenceKey === edit.areaRef) : undefined;
    const restoreArea = () =>
        perform(async () => {
            if (!undo || undo.revision !== revisionNumber) return;
            const result = await onRestorePrintArea({
                variantRef,
                sideRef: side.entityReferenceKey!,
                areaRef: undo.areaRef,
                expectedRevisionNumber: undo.revision
            });
            if (result) {
                setSelectedRef(result.areaRef ?? undo.areaRef);
                setUndo(null);
            } else setLocalError(T._('The deletion could not be undone.', 'Admin'));
        });
    const replaceImage = (file: File) =>
        perform(async () => {
            closeDialog('agent-replace-side-image');
            const expectedRevisionNumber = revisionNumber!;
            const dimensions = await readImageDimensions(file);
            const url = await uploadFile(file);
            const result = await onReplaceSideImage({ variantRef, sideRef: side.entityReferenceKey!, expectedRevisionNumber, url, ...dimensions });
            if (result) {
                setSelectedRef(null);
                setUndo(null);
                stopEditing();
            } else setLocalError(T._('The image could not be replaced.', 'Admin'));
        });
    const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setLocalError(T._('Select a valid image file.', 'Admin'));
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setLocalError(T._('Images must be smaller than 12 MB.', 'Admin'));
            return;
        }
        if (!hasScale && areas.length === 0) {
            void replaceImage(file);
            return;
        }
        showDialog(
            'agent-replace-side-image',
            <QuestionDialog
                title={T._('Replace side image', 'Admin')}
                message={T._(
                    'Print areas and scale are based on the current image. Replacing it will clear them for this side, so they can be configured again.',
                    'Admin'
                )}
                buttonYesLabel={T._('Replace image', 'Admin')}
                buttonNoLabel={T._('Cancel', 'Admin')}
                onYesClick={() => void replaceImage(file)}
                onNoClick={() => closeDialog('agent-replace-side-image')}
            />
        );
    };
    const sizeLabel = (rect: AreaRectangle, pixelsPerCm: number | null = hasScale ? scale! : null) =>
        `${numberText(rect.width / (pixelsPerCm ?? 1), 2)} × ${numberText(rect.height / (pixelsPerCm ?? 1), 2)} ${pixelsPerCm ? 'cm' : 'px'}`;

    // Switching side remounts this component, so an unsaved area edit would be lost.
    const canSwitchSide = !edit && !working;

    // The print-area actions, shown as chips in the conversation (portalled) rather than inside the preview.
    // The preview is read-only: every action describes the change to the agent, which then makes it in the conversation.
    const sideLabel = side.name || T._('current', 'Admin');
    const selectedIndex = selectedArea ? areas.indexOf(selectedArea) : -1;
    const selectedName = selectedArea ? areaName(selectedArea, selectedIndex) : '';
    const canRequest = Boolean(onRequestEdit) && !busy;
    const actionChips = (
        <>
            {selectedArea && (
                <ActionChip
                    type='button'
                    disabled={!canRequest}
                    onClick={() => onRequestEdit?.(stringFormat(T._('Edit the "{0}" print area on the "{1}" side.', 'Admin'), selectedName, sideLabel))}
                >
                    <Icon>
                        <EditIcon />
                    </Icon>
                    {T._('Edit area', 'Admin')}
                </ActionChip>
            )}
            <ActionChip
                type='button'
                disabled={!canRequest}
                onClick={() => onRequestEdit?.(stringFormat(T._('Replace the image of the "{0}" side.', 'Admin'), sideLabel))}
            >
                <Icon>
                    <ReplaceImageIcon />
                </Icon>
                {T._('Replace image', 'Admin')}
            </ActionChip>
            <ActionChip
                type='button'
                disabled={!canRequest}
                onClick={() => onRequestEdit?.(stringFormat(T._('Add a print area to the "{0}" side.', 'Admin'), sideLabel))}
            >
                <Icon>
                    <AddIcon />
                </Icon>
                {T._('Add print area', 'Admin')}
            </ActionChip>
        </>
    );

    return (
        <Layout>
            <StageBand>
                <Stage>
                    {sideNavigation && (
                        <>
                            <NavArrow
                                type='button'
                                $side='left'
                                aria-label={T._('Previous side', 'Admin')}
                                disabled={!canSwitchSide}
                                onClick={sideNavigation.onPrevious}
                            >
                                <Icon>
                                    <ChevronLeftIcon />
                                </Icon>
                            </NavArrow>
                            <NavArrow
                                type='button'
                                $side='right'
                                aria-label={T._('Next side', 'Admin')}
                                disabled={!canSwitchSide}
                                onClick={sideNavigation.onNext}
                            >
                                <Icon>
                                    <ChevronRightIcon />
                                </Icon>
                            </NavArrow>
                            <SidePosition>
                                {sideNavigation.position} / {sideNavigation.total}
                            </SidePosition>
                        </>
                    )}
                    {image?.url && (
                        <ZTooltip position='left' title={T._('Zoom artifact configuration', 'Admin')}>
                            <ZoomButton
                                type='button'
                                $side='right'
                                aria-label={T._('Zoom artifact configuration', 'Admin')}
                                onClick={() =>
                                    showDialog(
                                        ZOOM_DIALOG_ID,
                                        <AgentImageZoomDialog
                                            variants={
                                                zoomVariants?.length
                                                    ? zoomVariants
                                                    : [{ name: '', images: [{ url: image.url!, name: side.name || T._('Product side', 'Admin') }] }]
                                            }
                                            initialUrl={image.url!}
                                            onClose={() => closeDialog(ZOOM_DIALOG_ID)}
                                        />
                                    )
                                }
                            >
                                <Icon>
                                    <ZoomInIcon />
                                </Icon>
                            </ZoomButton>
                        </ZTooltip>
                    )}
                    {image?.url ? (
                        <ImageFrame
                            ref={stageRef}
                            $aspectRatio={imageSize.width && imageSize.height ? imageSize.width / imageSize.height : 1}
                            $maxHeight={240}
                            $drawing={Boolean(edit && !edit.areaRef && canResize)}
                            onPointerDown={(event) => {
                                if (edit && !edit.areaRef) beginDrag(event, 'draw', edit.rect);
                            }}
                            onPointerMove={onPointerMove}
                            onPointerUp={(event) => finishDrag(event)}
                            onPointerCancel={(event) => finishDrag(event, true)}
                            onLostPointerCapture={() => {
                                dragRef.current = null;
                            }}
                        >
                            <img src={image.url} alt={side.name || T._('Product side', 'Admin')} loading='lazy' draggable={false} />
                            {imageSize.width > 0 &&
                                imageSize.height > 0 &&
                                areas.map((area, index) => {
                                    const rect = rectangleOf(area, imageSize);
                                    if (!rect || (edit && area.entityReferenceKey === edit.areaRef)) return null;
                                    const selected = selectedArea === area && !edit;
                                    return (
                                        <Rectangle
                                            key={area.entityReferenceKey ?? index}
                                            $rect={rect}
                                            $image={imageSize}
                                            $selected={selected}
                                            $interactive={!edit && !busy}
                                            role='button'
                                            tabIndex={!edit && !busy ? 0 : -1}
                                            aria-label={`${areaName(area, index)} · ${sizeLabel(rect)}`}
                                            aria-pressed={selected}
                                            onPointerDown={(event) => {
                                                if (!canManipulate) return;
                                                startEditing(area);
                                                beginDrag(event, 'move', rect);
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    startEditing(area);
                                                }
                                            }}
                                        >
                                            <AreaLabel>
                                                {index + 1} · {areaName(area, index)}
                                            </AreaLabel>
                                        </Rectangle>
                                    );
                                })}
                            {edit && (
                                <Rectangle
                                    $rect={edit.rect}
                                    $image={imageSize}
                                    $active
                                    $interactive={canManipulate}
                                    role='group'
                                    tabIndex={0}
                                    aria-label={T._('Selected print area. Use arrow keys to move.', 'Admin')}
                                    onPointerDown={(event) => beginDrag(event, 'move', edit.rect)}
                                    onKeyDown={(event) => onGeometryKey(event)}
                                >
                                    {canResize &&
                                        HANDLES.map((handle) => (
                                            <Handle
                                                key={handle.key}
                                                type='button'
                                                $x={handle.x}
                                                $y={handle.y}
                                                $handle={handle.key}
                                                disabled={!canManipulate}
                                                aria-label={handle.label}
                                                onPointerDown={(event) => beginDrag(event, handle.key, edit.rect)}
                                                onKeyDown={(event) => onGeometryKey(event, handle.key)}
                                            />
                                        ))}
                                </Rectangle>
                            )}
                        </ImageFrame>
                    ) : (
                        <MissingImage generation={imageGeneration} />
                    )}
                </Stage>
            </StageBand>
            <DetailsColumn>
                <Section $gap='12px'>
                    {/* The settings of this side; its print areas are the part edited here. */}
                    <SectionTitle>
                        {T._('Side settings', 'Admin')}
                        {side.name ? ' · ' + side.name : ''}
                    </SectionTitle>
                    {edit ? (
                        <Stack $gap='10px'>
                            <SubTitle>
                                <span>{edit.areaRef ? T._('Edit area', 'Admin') : T._('New area', 'Admin')}</span>
                                <Muted>{sizeLabel(edit.rect, editScale)}</Muted>
                            </SubTitle>
                            <Hint>
                                {isSizeLocked
                                    ? T._(
                                          'Drag the area to move it. Its size on the image is locked while the scale is fixed. Arrow keys move by 1 pixel; Shift moves by 10.',
                                          'Admin'
                                      )
                                    : T._(
                                          'Drag the area to move it. Drag the handles to resize it, or enter exact dimensions. Arrow keys move by 1 pixel; Shift moves by 10.',
                                          'Admin'
                                      )}
                            </Hint>
                            {!edit.areaRef && !isSizeLocked && <Hint>{T._('You can also drag on the empty image to draw a new rectangle.', 'Admin')}</Hint>}
                            <Field>
                                {T._('Area name', 'Admin')}
                                <TextInput
                                    value={edit.name}
                                    maxLength={200}
                                    disabled={busy || staleEdit}
                                    onChange={(event) => setEdit({ ...edit, name: event.target.value })}
                                />
                            </Field>
                            <FieldGrid>
                                <Field>
                                    {T._('Width', 'Admin')} ({edit.unit})
                                    <TextInput
                                        inputMode='decimal'
                                        value={widthText}
                                        disabled={busy || staleEdit}
                                        aria-invalid={Boolean(geometryError)}
                                        onChange={(event) => changeDimension('width', event.target.value)}
                                    />
                                </Field>
                                <Field>
                                    {T._('Height', 'Admin')} ({edit.unit})
                                    <TextInput
                                        inputMode='decimal'
                                        value={heightText}
                                        disabled={busy || staleEdit}
                                        aria-invalid={Boolean(geometryError)}
                                        onChange={(event) => changeDimension('height', event.target.value)}
                                    />
                                </Field>
                                <Field>
                                    {T._('Units', 'Admin')}
                                    <Select
                                        value={edit.unit}
                                        disabled={busy || staleEdit}
                                        onChange={(event) => {
                                            const unit = event.target.value as 'cm' | 'px';
                                            // Without a scale, centimetres can only define one.
                                            setEdit({ ...edit, unit, keepScale: edit.keepScale || !editScale });
                                            syncDimensions(edit.rect, unit);
                                        }}
                                    >
                                        <option value='px'>px</option>
                                        <option value='cm'>cm</option>
                                    </Select>
                                </Field>
                                <Field>
                                    {T._('Proportions', 'Admin')}
                                    <Select
                                        value={edit.ratio ? 'current' : 'free'}
                                        disabled={busy || staleEdit || isSizeLocked}
                                        onChange={(event) => changeRatio(event.target.value)}
                                    >
                                        <option value='free'>{T._('Free', 'Admin')}</option>
                                        <option value='current'>
                                            {edit.ratio ? `${T._('Locked', 'Admin')} · ${numberText(edit.ratio)}:1` : T._('Lock current ratio', 'Admin')}
                                        </option>
                                        <option value='1'>1:1</option>
                                        <option value={4 / 3}>4:3</option>
                                        <option value={3 / 4}>3:4</option>
                                        <option value={16 / 9}>16:9</option>
                                    </Select>
                                </Field>
                            </FieldGrid>
                            {edit.unit === 'cm' && (
                                <Stack $gap='4px'>
                                    <Checkbox
                                        variant='compact-ai'
                                        label={T._('Keep scale fixed', 'Admin')}
                                        checked={edit.keepScale}
                                        disabled={busy || staleEdit || !editScale}
                                        onChange={(keepScale) => {
                                            // Resizing the area again goes back to the saved scale, dropping a size typed meanwhile.
                                            const pendingScale = !keepScale && hasScale ? null : edit.pendingScale;
                                            setEdit({ ...edit, keepScale, pendingScale });
                                            syncDimensions(edit.rect, edit.unit, pendingScale ?? (hasScale ? scale! : null));
                                        }}
                                    />
                                    <Hint>
                                        {!edit.keepScale
                                            ? T._('Changing the measurements resizes this area on the image.', 'Admin')
                                            : editScale
                                              ? T._(
                                                    'The area stays as drawn on the image: the measurements you enter change the real size of the side.',
                                                    'Admin'
                                                )
                                              : T._('This side has no scale yet. Enter the real size of the area to set it.', 'Admin')}
                                    </Hint>
                                    {edit.pendingScale && edit.pendingScale !== scale && (
                                        <Hint role='status'>
                                            {T._('New scale saved with the area', 'Admin')}: {numberText(edit.pendingScale, 4)} PPCM
                                        </Hint>
                                    )}
                                </Stack>
                            )}
                            {geometryError && <ErrorText role='alert'>{geometryError}</ErrorText>}
                            {staleEdit && (
                                <ErrorText role='alert'>
                                    {T._('The draft changed while you were editing. Cancel and reopen the area to use the latest version.', 'Admin')}
                                </ErrorText>
                            )}
                        </Stack>
                    ) : (
                        <>
                            <SubTitle>
                                <span>{T._('Print areas', 'Admin')}</span>
                            </SubTitle>
                            {areas.length > 0 ? (
                                <AreaList role='listbox' aria-label={T._('Print areas', 'Admin')}>
                                    {areas.map((area, index) => {
                                        const rect = rectangleOf(area, imageSize);
                                        const selected = area === selectedArea;
                                        const selectable = !busy && Boolean(area.entityReferenceKey);
                                        return (
                                            <AreaRow
                                                key={area.entityReferenceKey ?? index}
                                                role='option'
                                                aria-selected={selected}
                                                tabIndex={selectable ? 0 : -1}
                                                $selected={selected && areas.length > 1}
                                                onClick={() => selectable && setSelectedRef(area.entityReferenceKey!)}
                                                onKeyDown={(event) => {
                                                    if (selectable && (event.key === 'Enter' || event.key === ' ')) {
                                                        event.preventDefault();
                                                        setSelectedRef(area.entityReferenceKey!);
                                                    }
                                                }}
                                            >
                                                <AreaIndex>{index + 1}</AreaIndex>
                                                <AreaInfo>
                                                    <AreaName>
                                                        {side.name ? `${side.name} — ` : ''}
                                                        {areaName(area, index)}
                                                    </AreaName>
                                                    {rect && <Muted>{sizeLabel(rect)}</Muted>}
                                                </AreaInfo>
                                            </AreaRow>
                                        );
                                    })}
                                </AreaList>
                            ) : (
                                <Hint>{T._('No print areas yet.', 'Admin')}</Hint>
                            )}
                            {!disabled && !hasImageDimensions && image?.url && (
                                <Hint>{T._('Image dimensions are required before print areas can be edited.', 'Admin')}</Hint>
                            )}
                        </>
                    )}
                    {working && <Hint role='status'>{T._('Saving…', 'Admin')}</Hint>}
                    {localError && <ErrorText role='alert'>{localError}</ErrorText>}
                    <HiddenFileInput ref={fileInputRef} type='file' accept='image/*' onChange={onFileSelected} />
                </Section>
            </DetailsColumn>
            {actionsSlot === undefined ? <ChipBar>{actionChips}</ChipBar> : actionsSlot ? createPortal(actionChips, actionsSlot) : null}
        </Layout>
    );
};

/** Read-only side preview used by the grid of all sides: the image with its print areas, name and area count. */
export const AgentSideThumbnail: FC<{ side: ProductSide; selected: boolean; imageGeneration?: AgentGeneratedImage; onSelect: () => void }> = ({
    side,
    selected,
    imageGeneration,
    onSelect
}) => {
    const areas = side.areas ?? [];
    const image = side.image;
    const imageSize = { width: image?.widthPx ?? 0, height: image?.heightPx ?? 0 };
    const name = side.name || T._('Unnamed side', 'Admin');
    return (
        <Tile type='button' $selected={selected} aria-pressed={selected} onClick={onSelect}>
            <TileStage>
                {image?.url ? (
                    <ImageFrame $aspectRatio={imageSize.width && imageSize.height ? imageSize.width / imageSize.height : 1} $drawing={false} $maxHeight={140}>
                        <img src={image.url} alt={name} loading='lazy' draggable={false} />
                        {imageSize.width > 0 &&
                            imageSize.height > 0 &&
                            areas.map((area, index) => {
                                const rect = rectangleOf(area, imageSize);
                                return rect && <Rectangle key={area.entityReferenceKey ?? index} $rect={rect} $image={imageSize} $interactive={false} />;
                            })}
                    </ImageFrame>
                ) : (
                    <MissingImage generation={imageGeneration} compact />
                )}
            </TileStage>
            <TileFooter>
                <strong>{name}</strong>
                <Muted>
                    {areas.length} {areas.length === 1 ? T._('area', 'Admin') : T._('areas', 'Admin')}
                </Muted>
            </TileFooter>
        </Tile>
    );
};
